// Dev-only `globalThis.fetch` wrapper that records every HTTP request to
// the API registry. UNLIKE the Helius wrapper (which is passed into the
// @solana/web3.js Connection's `fetch` option — a clean per-instance
// chokepoint), the SDK uses the platform `fetch`, so we have to patch
// the global. The wrapper proxies to the original native fetch unchanged
// behaviour-wise — it just observes.
//
// Every fetch is recorded; the `kind` field distinguishes:
//   - `api`   — URL matched a configured `NEXT_PUBLIC_API_URL_*` origin
//   - `other` — anything else (Helius RPC, telemetry, image loads, etc.)
//   - `system` — synthetic records the instrumenter inserts (install
//                marker, config warnings)
//
// Capturing everything (not just API) means the panel always shows
// activity even when env vars are misconfigured, and lets engineers
// audit which third-party endpoints the app touches.
//
// Production: the consumer (`installApiInstrumentation`) is only called
// inside a `process.env.NODE_ENV !== 'production'` `if` block at the
// call site (see `providers/sdk-v2-provider.tsx`). Webpack/Turbopack DCE
// the call, the import becomes unused, and the module is tree-shaken
// out of the bundle. Zero prod overhead.

import { env } from '@/lib/config/env';

import { captureApiCallSite, recordApiCall, recordSystemMessage } from './api-registry';

interface FetchGlobals {
  __morfiApiInstrumented?: boolean;
}

/**
 * Extract the bare `URL.origin` from each configured API URL env var so
 * we can match a fetch's `parsed.origin` regardless of whether the
 * caller put `/api/v1` (or any other path prefix) on the env value or
 * not. The SDK can be configured with either form, and the actual
 * fetched URL may have a different path layout than the env hint —
 * matching on origin alone catches both cases.
 */
function collectApiOrigins(): readonly string[] {
  const candidates: readonly (string | undefined)[] = [
    env.NEXT_PUBLIC_API_URL_DEVNET,
    env.NEXT_PUBLIC_API_URL_MAINNET,
    env.NEXT_PUBLIC_API_URL_LOCAL,
  ];
  const out: string[] = [];
  for (const c of candidates) {
    if (typeof c !== 'string' || c.length === 0) continue;
    try {
      const origin = new URL(c).origin;
      if (!out.includes(origin)) out.push(origin);
    } catch {
      // SILENT-CATCH: invalid URL candidate is intentionally skipped during origin probing.
    }
  }
  return out;
}

function urlFromInput(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function bodyByteLength(body: BodyInit | null | undefined): number {
  if (body === null || body === undefined) return 0;
  if (typeof body === 'string') return body.length;
  if (body instanceof URLSearchParams) return body.toString().length;
  if (body instanceof FormData) return 0;
  if (body instanceof ArrayBuffer) return body.byteLength;
  if (body instanceof Blob) return body.size;
  if (ArrayBuffer.isView(body)) return body.byteLength;
  return 0;
}

/**
 * Best-effort capture of a request body as a string for the expandable
 * panel view. Returns `null` for non-string bodies (we don't materialise
 * Blob/ArrayBuffer/FormData since those can be large or expensive).
 */
function captureRequestBodyText(body: BodyInit | null | undefined): string | null {
  if (typeof body === 'string') return body;
  if (body instanceof URLSearchParams) return body.toString();
  return null;
}

/**
 * Read the response body without disturbing the consumer. We `clone()`
 * before reading so the caller still gets an unread body. Falls back to
 * `null` for binary content or read failures.
 *
 * **Streaming carve-out** (`text/event-stream`): SSE responses are
 * read-once long-lived streams. `await clone.text()` waits for the
 * stream to terminate — the consumer (`fetch-event-source`) cannot
 * receive the response object from `instrumentedFetch` until then,
 * which collapses progressive SSE delivery into a single burst at
 * stream-close. This regressed the AI-proposal Generate flow: the
 * Network tab showed events arriving progressively over ~15s, but
 * `onopen` / `onmessage` fired in a 200ms burst at the end.
 *
 * Skipping body capture for SSE costs DevToolkit visibility (the panel
 * shows no body for those calls) but preserves real-time delivery.
 */
function isStreamingResponse(response: Response): boolean {
  const contentType = response.headers.get('content-type') ?? '';
  return contentType.startsWith('text/event-stream');
}

async function captureResponseBodyText(response: Response): Promise<string | null> {
  if (isStreamingResponse(response)) {
    return null;
  }
  const contentType = response.headers.get('content-type') ?? '';
  // Skip binary content types — reading them is wasteful and the result
  // wouldn't render usefully in the panel.
  if (
    contentType.startsWith('image/') ||
    contentType.startsWith('video/') ||
    contentType.startsWith('audio/') ||
    contentType.startsWith('application/octet-stream')
  ) {
    return null;
  }
  try {
    const clone = response.clone();
    return await clone.text();
  } catch {
    // SILENT-CATCH: response already consumed or stream failed — no body
    return null;
  }
}

/**
 * Build a dedup-relevant fingerprint of the request body for non-
 * idempotent methods. POSTs with different bodies share the same
 * path+method+empty-query, so without body fingerprinting they look
 * identical to the burst detector. Including a cheap
 * digest of the body avoids false-positive DUPE flags.
 *
 * Returns `''` for non-string bodies (FormData, Blob, etc.) — those
 * stay coarse-grained, which is the best we can do without consuming
 * the body.
 */
function bodyFingerprint(body: BodyInit | null | undefined): string {
  if (typeof body !== 'string') return '';
  if (body.length <= 256) return body;
  // For larger bodies, use length + first/last 64 chars as a cheap
  // signature. Not cryptographic — just enough to distinguish typical
  // SDK batch payloads.
  return `${String(body.length)}::${body.slice(0, 64)}…${body.slice(-64)}`;
}

function parseFieldsParam(query: URLSearchParams): string {
  const f = query.get('fields');
  return f ?? '';
}

/**
 * Patch `globalThis.fetch` once. Idempotent — repeated calls are no-op.
 * No-op outside the browser (SSR has its own fetch boundary).
 *
 * Always records EVERY fetch (tagging by kind), not just ones matching
 * the API origin. This means the panel always shows activity regardless
 * of env-var configuration — if `NEXT_PUBLIC_API_URL_*` is wrong, you'll
 * see fetches tagged as `other` and can spot the misconfiguration.
 */
export function installApiInstrumentation(): void {
  if (typeof window === 'undefined') return;
  const g = globalThis as typeof globalThis & FetchGlobals;
  if (g.__morfiApiInstrumented === true) return;
  g.__morfiApiInstrumented = true;

  const origins = collectApiOrigins();
  recordSystemMessage(
    origins.length === 0
      ? 'API instrumentation installed — but NO origins matched any NEXT_PUBLIC_API_URL_* env var. Every fetch will appear tagged "other".'
      : `API instrumentation installed (matching ${String(origins.length)} origin${origins.length === 1 ? '' : 's'}: ${origins.join(', ')})`,
  );

  const originalFetch: typeof fetch = window.fetch.bind(window);

  window.fetch = async function instrumentedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const rawUrl = urlFromInput(input);

    let parsedOrNull: URL | null;
    try {
      // `new URL(string)` requires an absolute URL or a base. For relative
      // URLs (rare in client-side fetches), fall back to window.location.
      parsedOrNull = new URL(rawUrl, window.location.href);
    } catch {
      // SILENT-CATCH: malformed URL — proxy unchanged, don't record
      return originalFetch(input, init);
    }
    if (parsedOrNull === null) return originalFetch(input, init);
    const parsed: URL = parsedOrNull;

    const matchedOrigin = origins.includes(parsed.origin);
    // Default classification by origin; image-content responses
    // re-tag to `kind: 'image'` once we have the response headers
    // (see below). This means a fetch'd image asset shows up under
    // the "Images" filter regardless of which origin it came from.
    let kind: 'api' | 'image' | 'other' = matchedOrigin ? 'api' : 'other';
    const method = (init?.method ?? 'GET').toUpperCase();
    const path = parsed.pathname;
    const query = parsed.search.startsWith('?') ? parsed.search.slice(1) : parsed.search;
    const fields = parseFieldsParam(parsed.searchParams);
    const requestBytes = bodyByteLength(init?.body);
    // Only fingerprint bodies for non-idempotent methods — GETs share
    // the empty digest, which is fine because their query string already
    // distinguishes them.
    const bodyDigest =
      method === 'POST' || method === 'PUT' || method === 'PATCH'
        ? bodyFingerprint(init?.body)
        : '';
    const requestBody = captureRequestBodyText(init?.body);
    const callSite = captureApiCallSite();
    const start = performance.now();

    let response: Response;
    try {
      response = await originalFetch(input, init);
    } catch (err: unknown) {
      // SILENT-CATCH: instrumentation records the failure then rethrows below — actual error reporting is the caller's responsibility.
      const elapsed = performance.now() - start;
      recordApiCall({
        kind,
        method,
        origin: parsed.origin,
        path,
        query,
        fields,
        requestBytes,
        responseBytes: null,
        status: 0,
        ok: false,
        latencyMs: elapsed,
        callSite,
        bodyDigest,
        requestBody,
        responseBody: null,
      });
      throw err;
    }
    const elapsed = performance.now() - start;
    const contentLengthRaw = response.headers.get('content-length');
    const responseBytes = contentLengthRaw !== null ? Number(contentLengthRaw) : null;
    const contentTypeHeader = response.headers.get('content-type') ?? '';
    if (contentTypeHeader.toLowerCase().startsWith('image/')) {
      kind = 'image';
    }
    // Clone synchronously, but never hold the consumer behind diagnostic body capture.
    void captureResponseBodyText(response).then((responseBody) =>
      recordApiCall({
        kind,
        method,
        origin: parsed.origin,
        path,
        query,
        fields,
        requestBytes,
        responseBytes:
          responseBytes !== null && Number.isFinite(responseBytes) ? responseBytes : null,
        status: response.status,
        ok: response.ok,
        latencyMs: elapsed,
        callSite,
        bodyDigest,
        requestBody,
        responseBody,
      }),
    );

    return response;
  };
}
